import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, Layout, Modal, message } from 'antd';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  BaseCatalogueHeader,
  TemplateRenderer,
  ThemeLoader,
  preloadTemplate,
  resolveCatalogueTemplate,
  TemplateSchemaError,
  useThemeAssets,
  useVisualTemplate,
  type ResolvedCatalogueTemplate,
  type VisualTemplate,
} from '@eshop/templates';

export const EShopTitleContext = createContext<{
  setCustomTitle: (title: string | null) => void;
}>({
  setCustomTitle: () => {},
});
import { CatalogueTemplateContext } from '@eshop/templates/TemplateContext';
import type { CatalogueTemplate } from '@eshop/templates/types';
import TemplateConfigurator from '@eshop/templates/TemplateConfigurator.tsx';
import { createDefinitionApplier } from '@eshop/templates/applyTemplateDefinition';
import { useTemplateDraft } from '@eshop/templates/hooks/useTemplateDraft';
import { DraftOverridesProvider, type DraftTemplateOverrides } from '@eshop/templates/context/DraftOverridesContext';
import { CheckoutLayoutProvider } from '@eshop/hooks/useCheckoutLayout.tsx';
import {
  CatalogueAppearanceContext,
  DEFAULT_CATALOGUE_DESIGN,
  getCatalogueDesignClass,
  isValidCatalogueDesign,
  type CatalogueDesignKey,
} from '@eshop/designs';
import { useAppDispatch, useAppSelector } from '@store/store.tsx';
import {
  selectSiteSettings,
  selectSiteTitle,
  updateSiteSettings,
  type SiteSettings,
} from '@store/slices/settingsSlice.tsx';
import { fetchCfgCatalogueDataAsync } from '@store/slices/eshopSlice.tsx';
import { EshopFloatButtons } from '@eshop/components/floating-buttons';
import SoftCommerceBottomNav from '@eshop/templates/themes/soft-commerce/components/SoftCommerceBottomNav';
import EShopFooter from './EShopFooter.tsx';
import './EShopFooter.css';
import ThemeToggle from './ThemeToggle';
import { useThemeMode } from '@theme/hooks/useThemeMode.tsx';
import { useCanEditCatalogue } from '@theme/hooks/useCanEditCatalogue.ts';
import { useUnifiedDocumentTitle } from './useUnifiedDocumentTitle.ts';
import type { CatalogueTemplateDefinition } from '@eshop/templates/schema';
const { Content } = Layout;

const PANEL_FOOTER_TOKEN = '--catalogue-footer-visibility';
const HIDDEN_TOKEN_VALUE = 'none';

const resolveHeroVisibility = (definition: CatalogueTemplateDefinition | null | undefined): boolean => {
  if (!definition?.regions) {
    return true;
  }
  return definition.regions.includes('hero');
};

const resolveFooterVisibility = (definition: CatalogueTemplateDefinition | null | undefined): boolean => {
  const tokenValue = definition?.layout?.panel?.tokens?.[PANEL_FOOTER_TOKEN];
  if (typeof tokenValue !== 'string') {
    return true;
  }
  return tokenValue.trim().toLowerCase() !== HIDDEN_TOKEN_VALUE;
};

const normalizeRatioLabel = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const slashMatch = trimmed.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (slashMatch) {
    return `${slashMatch[1]}:${slashMatch[2]}`;
  }

  const colonMatch = trimmed.match(/^(\d+)\s*:\s*(\d+)$/);
  if (colonMatch) {
    return `${colonMatch[1]}:${colonMatch[2]}`;
  }

  return trimmed;
};

interface CatalogueLayoutFrameProps {
  resolvedTheme: 'light' | 'dark';
  requestedDesign: CatalogueDesignKey;
  effectiveDesign: CatalogueDesignKey;
  isStaff: boolean;
  canEditCatalogue: boolean;
  editTemplate: boolean;
  template: CatalogueTemplate;
  siteSettings: SiteSettings | null;
  templateWarning: string | null;
  children?: ReactNode;
}

const CatalogueLayoutFrame = ({
  resolvedTheme,
  requestedDesign,
  effectiveDesign,
  isStaff,
  canEditCatalogue,
  editTemplate,
  template,
  siteSettings,
  templateWarning,
  children,
}: CatalogueLayoutFrameProps) => {
  const themeAssets = useThemeAssets();
  const layoutClassName = `eshop-layout theme-${resolvedTheme} ${themeAssets.designClassName || getCatalogueDesignClass(effectiveDesign)
    }`;

  const HeaderComponent = template.visualTemplate?.Header;

  const headerOverrides = useMemo(() => {
    if (!HeaderComponent || !canEditCatalogue || !editTemplate) {
      return undefined;
    }

    return {
      slots: {
        configurator: <TemplateConfigurator />,
      },
    };
  }, [HeaderComponent, canEditCatalogue, editTemplate]);

  return (
    <CheckoutLayoutProvider>
      <Layout
        className={layoutClassName}
        data-theme={resolvedTheme}
        data-design={themeAssets.resolvedDesign}
        data-requested-design={requestedDesign}
        data-effective-design={effectiveDesign}
        data-template={template.visualTemplate?.key}
        style={{ minHeight: '100vh' }}
      >
        <div className="theme-toggle-floating theme-toggle-floating--eshop">
          <ThemeToggle size="small" />
        </div>
        {HeaderComponent && (
          <BaseCatalogueHeader
            component={HeaderComponent}
            overrides={headerOverrides}
          />
        )}
        <Content className="eshop-content">
          <div className="eshop-content__inner">
            {isStaff && templateWarning ? (
              <Alert
                type="warning"
                showIcon
                title="Se aplicó el diseño predeterminado"
                description={templateWarning}
                className="catalogue-template-warning"
                style={{ marginBottom: 16 }}
              />
            ) : null}
            {children}
          </div>
        </Content>
        <EShopFooter
          settings={siteSettings}
          visible={template.footerVisible ?? true}
        />
        {/* TODO: Temporalmente queda así, pero se debe analizar para independizar aún más los diseños. */}
        {effectiveDesign === 'soft-commerce' ? <SoftCommerceBottomNav /> : null}
        <EshopFloatButtons />
      </Layout>
    </CheckoutLayoutProvider>
  );
};

interface LayoutWithTemplateProps {
  template: CatalogueTemplate | null;
  resolvedTheme: 'light' | 'dark';
  requestedDesign: CatalogueDesignKey;
  effectiveDesign: CatalogueDesignKey;
  isStaff: boolean;
  canEditCatalogue: boolean;
  editTemplate: boolean;
  siteSettings: SiteSettings | null;
  templateWarning: string | null;
  storeHeroVisible: boolean;
  footerVisible: boolean;
  topBannerPosition: 'grid' | 'hero';
  bannerAspectRatio: string | null;
  catalogueLinkStyle?: 'text' | 'icon' | 'both';
  catalogueLinkText?: string;
  children?: ReactNode;
}

const LayoutWithTemplate = ({
  template,
  resolvedTheme,
  requestedDesign,
  effectiveDesign,
  isStaff,
  canEditCatalogue,
  editTemplate,
  siteSettings,
  templateWarning,
  storeHeroVisible,
  footerVisible,
  topBannerPosition,
  bannerAspectRatio,
  catalogueLinkStyle,
  catalogueLinkText,
  children,
}: LayoutWithTemplateProps) => {
  const visualTemplate = useVisualTemplate();

  const templateValue = useMemo(() => {
    if (!template) {
      return null;
    }

    return {
      ...template,
      visualTemplate,
      storeHeroVisible,
      footerVisible,
      topBannerPosition,
      bannerAspectRatio,
      catalogueLinkStyle,
      catalogueLinkText,
    };
  }, [template, visualTemplate, storeHeroVisible, footerVisible, topBannerPosition, bannerAspectRatio, catalogueLinkStyle, catalogueLinkText]);

  if (!templateValue) {
    return null;
  }

  return (
    <CatalogueTemplateContext.Provider value={templateValue}>
      <CatalogueLayoutFrame
        resolvedTheme={resolvedTheme}
        requestedDesign={requestedDesign}
        effectiveDesign={effectiveDesign}
        isStaff={isStaff}
        canEditCatalogue={canEditCatalogue}
        editTemplate={editTemplate}
        template={templateValue}
        siteSettings={siteSettings}
        templateWarning={templateWarning}
      >
        {children}
      </CatalogueLayoutFrame>
    </CatalogueTemplateContext.Provider>
  );
};

// Layout para el e-commerce público
const EShopLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const [customTitle, setCustomTitle] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const templateName = searchParams.get('template') || 'classic';
  const editTemplate = searchParams.get('editTemplate') === '1';
  const isStaff = useAppSelector(s => s.user.isStaff);
  const canEditCatalogue = useCanEditCatalogue();
  const dispatch = useAppDispatch();
  const site = useAppSelector(selectSiteSettings);
  const siteTitle = useAppSelector(selectSiteTitle);
  const designOverrides = site?.catalogue_template_overrides ?? null;
  const { resolvedTheme } = useThemeMode();

  useEffect(() => {
    dispatch(fetchCfgCatalogueDataAsync()).catch(() => undefined);
  }, [dispatch]);

  const defaultDesign = useMemo<CatalogueDesignKey>(() => {
    const configuredDesign = site?.catalogue_design;
    if (isValidCatalogueDesign(configuredDesign)) {
      return configuredDesign;
    }
    return DEFAULT_CATALOGUE_DESIGN;
  }, [site?.catalogue_design]);

  const designParam = searchParams.get('catalogDesign');
  const [design, setDesign] = useState<CatalogueDesignKey>(() => {
    if (isValidCatalogueDesign(designParam)) {
      return designParam;
    }
    return defaultDesign;
  });

  const [resolution, setResolution] = useState<ResolvedCatalogueTemplate | null>(null);
  const [templateWarning, setTemplateWarning] = useState<string | null>(null);
  const [runtimeDesign, setRuntimeDesign] = useState<CatalogueDesignKey>(defaultDesign);

  useEffect(() => {
    const nextDesign = isValidCatalogueDesign(designParam) ? designParam : defaultDesign;
    setDesign(nextDesign);
  }, [designParam, defaultDesign]);

  const handleDesignChange = useCallback(
    (next: CatalogueDesignKey) => {
      const alreadyActive = next === design;
      if (alreadyActive && site?.catalogue_design === next) {
        return;
      }

      const previousDesign = design;
      const previousParams = new URLSearchParams(searchParams);

      if (!alreadyActive) {
        setDesign(next);
        const params = new URLSearchParams(previousParams);
        if (next === defaultDesign) {
          params.delete('catalogDesign');
        } else {
          params.set('catalogDesign', next);
        }
        setSearchParams(params, { replace: true });
      }

      if (canEditCatalogue && site?.catalogue_design !== next) {
        Modal.confirm({
          title: 'Guardar diseño del catálogo',
          content: '¿Deseás aplicar este diseño para todos los usuarios de la tienda?',
          okText: 'Guardar',
          cancelText: 'Seguir probando',
          centered: true,
          onOk: () =>
            dispatch(updateSiteSettings({ catalogue_design: next }))
              .unwrap()
              .then(() => {
                message.success('Diseño de catálogo actualizado');
              })
              .catch((error) => {
                message.error('No se pudo guardar el diseño del catálogo');
                if (!alreadyActive) {
                  setDesign(previousDesign);
                  setSearchParams(previousParams, { replace: true });
                }
                throw error;
              }),
        });
      }
    },
    [
      design,
      searchParams,
      setSearchParams,
      defaultDesign,
      canEditCatalogue,
      dispatch,
      site?.catalogue_design,
    ],
  );

  const appearanceValue = useMemo(
    () => ({
      design,
      setDesign: handleDesignChange,
    }),
    [design, handleDesignChange],
  );

  useEffect(() => {
    let cancelled = false;

    const loadResolution = async (designKey: CatalogueDesignKey, allowWarning: boolean): Promise<void> => {
      try {
        const result = await resolveCatalogueTemplate(templateName, {
          design: designKey,
          fallbackDesign: defaultDesign,
          definitionDesign: designKey,
          defaultDefinitionDesign: defaultDesign,
          publishedOverrides: designOverrides ?? undefined,
        });

        if (cancelled) {
          return;
        }

        setResolution(result);
        if (allowWarning) {
          setTemplateWarning(null);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (error instanceof TemplateSchemaError) {
          console.error(
            `Error al validar la definición declarativa para "${designKey}"`,
            { reason: error.reason, issues: error.issues, error },
          );

          if (allowWarning && designKey !== defaultDesign) {
            const issuesText = error.issues && error.issues.length > 0 ? error.issues.join(' ') : error.message;
            setTemplateWarning(
              `No se pudo validar la definición declarativa para «${designKey}»: ${issuesText}. Se usará «${defaultDesign}».`,
            );
            await loadResolution(defaultDesign, false);
            return;
          }
        } else if (process.env.NODE_ENV !== 'production') {
          console.error('Error al resolver el template declarativo', error);
        }

        setResolution(null);
      }
    };

    setTemplateWarning(null);
    loadResolution(design, true);

    return () => {
      cancelled = true;
    };
  }, [templateName, design, defaultDesign, designOverrides]);

  useEffect(() => {
    preloadTemplate(defaultDesign).catch(() => undefined);
  }, [defaultDesign]);

  useEffect(() => {
    if (design !== defaultDesign) {
      preloadTemplate(design).catch(() => undefined);
    }
  }, [design, defaultDesign]);

  const templateForLayout = resolution?.template ?? null;
  const effectiveDesign = useMemo<CatalogueDesignKey>(() => {
    const resolvedDesign = resolution?.design;
    if (resolvedDesign && isValidCatalogueDesign(resolvedDesign)) {
      return resolvedDesign;
    }
    return defaultDesign;
  }, [resolution?.design, defaultDesign]);

  useEffect(() => {
    setRuntimeDesign((current) => (current === effectiveDesign ? current : effectiveDesign));
  }, [effectiveDesign]);

  useEffect(() => {
    setRuntimeDesign(defaultDesign);
  }, [defaultDesign]);

  const definitionApplier = useMemo(
    () => createDefinitionApplier(resolution?.definition ?? null, { displayNameSuffix: 'Runtime' }),
    [resolution?.definition],
  );

  const draft = useTemplateDraft({
    template: canEditCatalogue ? templateForLayout?.name ?? null : null,
    design: canEditCatalogue ? resolution?.design ?? null : null,
    baseDefinition: resolution?.definition ?? null,
    publishedOverrides: designOverrides,
  });

  const storeHeroVisible = useMemo(() => {
    if (canEditCatalogue && editTemplate) {
      return draft.panel.storeHeroVisible;
    }
    return resolveHeroVisibility(resolution?.definition ?? null);
  }, [canEditCatalogue, editTemplate, draft.panel.storeHeroVisible, resolution?.definition]);

  const footerVisible = useMemo(() => {
    if (canEditCatalogue && editTemplate) {
      return draft.panel.footerVisible;
    }
    return resolveFooterVisibility(resolution?.definition ?? null);
  }, [canEditCatalogue, editTemplate, draft.panel.footerVisible, resolution?.definition]);

  const topBannerPosition = useMemo((): 'grid' | 'hero' => {
    if (canEditCatalogue && editTemplate) {
      return draft.panel.topBannerPosition;
    }
    // Default is 'grid' - read from saved definition if available
    const tokenValue = resolution?.definition?.layout?.panel?.tokens?.['--catalogue-top-banner-position'];
    const normalized = typeof tokenValue === 'string' ? tokenValue.trim().toLowerCase() : '';
    return normalized === 'hero' ? 'hero' : 'grid';
  }, [canEditCatalogue, editTemplate, draft.panel.topBannerPosition, resolution?.definition]);

  const bannerAspectRatio = useMemo(() => {
    const current = (canEditCatalogue && editTemplate)
      ? draft.style.bannerAspectClass
      : resolution?.definition?.layout?.panel?.tokens?.['--catalogue-banner-aspect'];

    if (typeof current === 'string' && current.trim()) {
      const trimmed = current.trim();
      if (trimmed.startsWith('aspect_')) {
        return trimmed;
      }
      const normalized = normalizeRatioLabel(trimmed);
      if (normalized) {
        const [width, height] = normalized.split(':');
        if (width && height) {
          return `aspect_${width}-${height}`;
        }
      }
    }

    return 'aspect_5-1';
  }, [canEditCatalogue, editTemplate, draft.style.bannerAspectClass, resolution?.definition]);

  // Extract catalogueLinkStyle/Text from definition
  const catalogueLinkStyle = resolution?.definition?.catalogueLinkStyle;
  const catalogueLinkText = resolution?.definition?.catalogueLinkText;

  const draftApplier = useMemo(
    () => (canEditCatalogue && editTemplate ? draft.applyToTemplate : null),
    [draft.applyToTemplate, editTemplate, canEditCatalogue],
  );

  // Compute draft overrides for live preview - use draft from THIS layout, not TemplateConfigurator
  const draftOverridesValue = useMemo<DraftTemplateOverrides | null>(() => {
    if (!canEditCatalogue || !editTemplate) {
      return null;
    }
    return {
      itemAspectRatio: draft.style.cardAspectClass,
      itemCardSize: draft.style.itemCardSize,
      isMinimized: draft.isMinimized,
      // hasUnsavedChanges is tracked in TemplateConfigurator, not in useTemplateDraft
      // For the floating button, we'll rely on isMinimized state only
      hasUnsavedChanges: false,
    };
  }, [canEditCatalogue, editTemplate, draft.style.cardAspectClass, draft.style.itemCardSize, draft.isMinimized]);

  const transformTemplate = useCallback(
    (visualTemplate: VisualTemplate) => {
      let next = definitionApplier ? definitionApplier(visualTemplate) : visualTemplate;
      if (draftApplier) {
        next = draftApplier(next);
      }
      return next;
    },
    [definitionApplier, draftApplier],
  );

  const selectedCategory = useAppSelector((state) => state.eshop.data.selectedCategory);

  const templatePageTitle = useMemo(() => {
    if (customTitle) {
      return customTitle;
    }

    if (pathname.includes('/eshop/catalogue')) {
      return 'Catálogo';
    }
    if (pathname.includes('/eshop/category/')) {
      return selectedCategory?.name || 'Catálogo';
    }
    if (pathname.includes('/eshop/cart')) {
      return 'Carrito';
    }
    if (pathname.includes('/eshop/checkout')) {
      return 'Finalizar Compra';
    }
    if (pathname.includes('/eshop/orders/') || pathname.includes('/eshop/checkout/preview/')) {
      return 'Pedido';
    }
    if (pathname.includes('/eshop/detail/')) {
      const match = pathname.match(/\/eshop\/detail\/([^\/]+)/);
      if (match) {
        const slug = match[1];
        const namePart = slug.split('_')[0];
        if (namePart) {
          return namePart
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        }
      }
      return 'Producto';
    }

    const metaLabel = templateForLayout?.visualTemplate?.meta?.label;
    if (typeof metaLabel === 'string' && metaLabel.trim()) {
      return metaLabel.trim();
    }
    const templateName = templateForLayout?.name;
    if (typeof templateName === 'string' && templateName.trim()) {
      return templateName.trim();
    }
    return null;
  }, [customTitle, pathname, selectedCategory, templateForLayout]);

  const eshopTitleOptions = useMemo(() => ({ defaultPageTitle: 'E-shop' }), []);
  useUnifiedDocumentTitle(templatePageTitle, siteTitle, eshopTitleOptions);

  if (!templateForLayout) {
    return null;
  }

  return (
    <EShopTitleContext.Provider value={{ setCustomTitle }}>
      <CatalogueAppearanceContext.Provider value={appearanceValue}>
        <DraftOverridesProvider value={draftOverridesValue}>
          <ThemeLoader design={runtimeDesign} fallbackDesign={defaultDesign}>
            <TemplateRenderer
              design={effectiveDesign}
              fallbackDesign={defaultDesign}
              transformTemplate={transformTemplate}
              onResolvedDesign={(resolved, meta) => {
                setRuntimeDesign((current) => (current === resolved ? current : resolved));
                if (meta.usedFallback && !templateWarning) {
                  setTemplateWarning(
                    `No se pudo cargar el diseño «${design}» en el runtime. Se utiliza «${resolved}» como fallback.`,
                  );
                }
              }}
            >
              <LayoutWithTemplate
                template={templateForLayout}
                resolvedTheme={resolvedTheme}
                requestedDesign={design}
                effectiveDesign={runtimeDesign}
                isStaff={isStaff}
                canEditCatalogue={canEditCatalogue}
                editTemplate={editTemplate}
                siteSettings={site}
                templateWarning={isStaff ? templateWarning : null}
                storeHeroVisible={storeHeroVisible}
                footerVisible={footerVisible}
                topBannerPosition={topBannerPosition}
                bannerAspectRatio={bannerAspectRatio}
                catalogueLinkStyle={catalogueLinkStyle}
                catalogueLinkText={catalogueLinkText}
              >
                {children}
              </LayoutWithTemplate>
            </TemplateRenderer>
          </ThemeLoader>
        </DraftOverridesProvider>
      </CatalogueAppearanceContext.Provider>
    </EShopTitleContext.Provider>
  );
};

export default EShopLayout;
